import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import './styles/CartPage.css';

const CartPage: React.FC = () => {
  const { cart, removeFromCart, updateQuantity, cartTotal, clearCart } = useCart();
  const navigate = useNavigate();

  if (cart.length === 0) {
    return (
      <div className="cart-page">
        <div className="empty-cart">
          <svg className="empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <h1 className="empty-title font-serif">Your cart is empty</h1>
          <p className="empty-message">Add some cozy items to get started!</p>
          <button onClick={() => navigate('/')} className="continue-shopping">Browse Products</button>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="cart-header">
        <h1 className="page-title font-serif">Shopping Cart</h1>
        <button onClick={clearCart} className="clear-cart-button">
          Clear Cart
        </button>
      </div>

      <div className="cart-content">
        {/* Cart Items */}
        <div className="cart-items-section">
          <div className="cart-items">
            {cart.map((item) => (
              <div key={item.id} className="cart-item">
                <img src={item.image} alt={item.name} className="item-image" />
                
                <div className="item-details">
                  <h3 className="item-name">{item.name}</h3>
                  <p className="item-description">{item.description}</p>
                  <p className="item-category">{item.category}</p>
                </div>

                <div className="item-price-info">
                  <div className="unit-price">${item.price.toFixed(2)} each</div>
                  <div className="item-subtotal">${(item.price * item.quantity).toFixed(2)}</div>
                </div>

                <div className="item-actions">
                  <div className="quantity-controls">
                    <button 
                      className="qty-button"
                      onClick={() => updateQuantity(item.id, Math.max(1, item.quantity - 1))}
                      aria-label="Decrease quantity"
                    >
                      −
                    </button>
                    <span className="qty-value">{item.quantity}</span>
                    <button 
                      className="qty-button"
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      aria-label="Increase quantity"
                    >
                      +
                    </button>
                  </div>
                  
                  <button 
                    className="remove-button"
                    onClick={() => removeFromCart(item.id)}
                    aria-label="Remove item"
                  >
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>

          <button onClick={() => navigate('/')} className="continue-shopping-link">
            ← Continue Shopping
          </button>
        </div>

        {/* Cart Summary */}
        <div className="cart-summary">
          <h2 className="summary-title font-serif">Order Summary</h2>
          
          <div className="summary-details">
            <div className="summary-row">
              <span>Subtotal ({cart.length} {cart.length === 1 ? 'item' : 'items'}):</span>
              <span>${cartTotal.toFixed(2)}</span>
            </div>
            
            <div className="shipping-estimate">
              <div className="estimate-label">Shipping:</div>
              <div className="estimate-text">Calculated at checkout</div>
            </div>

            {cartTotal >= 100 && (
              <div className="free-shipping-banner">
                <svg className="check-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                You qualify for free standard shipping!
              </div>
            )}

            {cartTotal < 100 && (
              <div className="threshold-notice">
                Spend ${(100 - cartTotal).toFixed(2)} more for free standard shipping
              </div>
            )}

            <div className="summary-total">
              <span>Estimated Total:</span>
              <span className="total-amount">${cartTotal.toFixed(2)}</span>
            </div>

            <button 
              onClick={() => navigate('/checkout')} 
              className="checkout-button"
            >
              Proceed to Checkout
              <svg className="arrow-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <div className="secure-checkout">
              <svg className="lock-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Secure Checkout
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;